import React, { useEffect, useState } from 'react';
import { Form, Image, InputGroup } from 'react-bootstrap';
import ReactApexChart from 'react-apexcharts';
import DatePicker from 'react-datepicker';

// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { ptBR } from 'date-fns/locale';

import { useToast } from '../hooks/toast';
import DashboardFeedback from '../components/DashboardFeedback';
import DashboardUserAtendiment from '../components/DashboardUserAtendiment';
import FiltroIcon from '../assets/icon/filtro.svg';
import 'react-datepicker/dist/react-datepicker.css';

import {
  IResumoAtendimento,
  IResumoAtendimentoPeriodo,
} from '../interfaces/IResumoAtendimento';

import api from '../services/apiClient';
import { getInitials, calculateColumnWidth } from '../services/function';
import { formatLocalDateTime } from '../utils/dateTime';

const Dashboard: React.FC = () => {
  const { addToast } = useToast();
  const [atendimentoData, setAtendimentoData] = useState<IResumoAtendimento>(
    {} as IResumoAtendimento
  );
  const [showDashboardFeedback, setShowDashboardFeedback] = useState(true);
  const periodoGrafico = [
    { qtdeDias: 1, Nome: 'Diário' },
    { qtdeDias: 7, Nome: 'Semanal' },
    { qtdeDias: 30, Nome: 'Mensal' },
    { qtdeDias: 365, Nome: 'Anual' },
  ];
  const [indexAtual, setIndexAtual] = useState(0);
  const [dataFiltro, setDataFiltro] = useState<Date | null>(new Date());
  const [openCalendar, setOpenCalendar] = useState(false);

  const valores =
    atendimentoData?.qtdeAtendimentoPeriodo?.map(
      (item) => item.qtdeAtendimento
    ) || [];

  const maxValor = Math.max(...valores, 0); // garante ao menos 0
  const intervalo = 5;
  const tickAmount = Math.ceil(maxValor / intervalo);

  const dataChartBar = {
    series: [
      {
        name: 'Atendimentos',
        data:
          atendimentoData?.qtdeAtendimentoPeriodo?.map(
            (item: IResumoAtendimentoPeriodo) => item.qtdeAtendimento
          ) || [],
      },
    ],
    options: {
      chart: {
        toolbar: {
          show: false,
        },
        type: 'bar' as const,
        zoom: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          dataLabels: {
            position: 'top', // top, center, bottom
          },
          columnWidth: '60%',
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        categories:
          atendimentoData?.qtdeAtendimentoPeriodo?.map(
            (item: IResumoAtendimentoPeriodo) => {
              if (indexAtual === 2) {
                return item.semana;
              }
              if (indexAtual === 3) {
                return item.mes;
              }

              return formatLocalDateTime(item.dataAtendimento, 'dd/MM/yyyy');
            }
          ) || [],
      },
      yaxis: {
        min: 0,
        max: maxValor,
        tickAmount: tickAmount > 0 ? tickAmount : 1,
        labels: {
          formatter: (val: number) => val.toFixed(0),
        },
      },
    },
  };

  const numBarsUsuario = atendimentoData.qtdeAtendimentoUsuario?.length || 1;
  const minWidth = 5;
  const maxWidth = 30;

  const dataChartBarUsuario = {
    options: {
      chart: {
        type: 'bar' as const,
        zoom: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          distributed: true,
          columnWidth: calculateColumnWidth(numBarsUsuario, minWidth, maxWidth),
        },
      },
      toolbar: {
        show: false,
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
      xaxis: {
        categories: atendimentoData.qtdeAtendimentoUsuario?.map(
          (item) => item.nomeUsuario
        ),
        labels: {
          style: {
            fontSize: '1rem', // Tamanho da fonte
            fontWeight: 'bold', // Negrito
          },
        },
      },
    },
    series: [
      {
        name: 'Qtde de Atendimentos',
        data:
          atendimentoData.qtdeAtendimentoUsuario?.map((item) => ({
            x: getInitials(item.nomeUsuario),
            y: item.qtdeTotalAtendimento,
          })) || [],
      },
    ],
  };

  const loadDataDashboard = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('@LiveChat:token');

      const mesAtual = Number(dataFiltro?.getMonth()) + 1;
      const anoAtual = dataFiltro?.getFullYear();

      const response = await api.get(
        `dashboard/${periodoGrafico[indexAtual].qtdeDias}/${mesAtual}/${anoAtual}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setAtendimentoData(response.data.data);
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    }
  };

  useEffect(() => {
    loadDataDashboard();
  }, [indexAtual, dataFiltro]);

  return (
    <div className="d-flex flex-column h-100 pb-1 ms-2">
      <div className="d-flex align-items-center justify-content-between">
        <h1 style={{ fontSize: '2.5rem', fontWeight: '600' }}>Dashboard</h1>
        <div className="d-flex align-items-center">
          {indexAtual >= 2 && (
            <div className="d-flex align-items-center justify-content-center">
              <p
                className="m-0 p-0 me-2"
                style={{ fontSize: '1.2rem', fontWeight: 'bold' }}
              >
                Filtrar por data:
              </p>
              <DatePicker
                className="form-control p-1"
                selected={dataFiltro}
                onChange={(date) => {
                  setDataFiltro(date);
                  setOpenCalendar(false);
                }}
                onSelect={() => setOpenCalendar(false)}
                open={openCalendar}
                onInputClick={() => setOpenCalendar(true)}
                {...(indexAtual === 2
                  ? { showMonthYearPicker: true }
                  : { showYearPicker: true })}
                dateFormat={indexAtual === 2 ? 'MM/yyyy' : 'yyyy'}
                locale={ptBR}
                customInput={
                  <input
                    style={{
                      width: '90px', // ou "auto" com `minWidth` se preferir
                      padding: '6px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                    }}
                  />
                }
              />
            </div>
          )}

          <p
            className="m-0 ms-2"
            style={{ fontSize: '1.68rem', fontWeight: '600' }}
          >
            {periodoGrafico[indexAtual].Nome}
          </p>
          <Image
            src={FiltroIcon}
            alt="FiltroIcon"
            className="ms-2 clickableImage"
            style={{ width: '2rem' }}
            onClick={() => {
              setIndexAtual((prev) => (prev + 1) % periodoGrafico.length);
              setDataFiltro(new Date());
            }}
            role="button"
          />
        </div>
      </div>
      <div className="d-flex flex-direction-row h-100">
        <section className="col-7 d-flex">
          <div className="d-flex flex-column w-100">
            <div className="d-flex justify-content-between">
              <div
                style={{
                  width: '20.6rem',
                  height: '6.5rem',
                  backgroundColor: '#0075FF',
                  paddingLeft: '1.5rem',
                  paddingRight: '1.5rem',
                  paddingTop: '2rem',
                  paddingBottom: '2rem',
                  borderRadius: '0.68rem',
                  boxShadow: '0px 1px 8px 2px rgba(0, 0, 0, 0.25)',
                }}
                className="d-flex align-items-center text-white"
              >
                <span
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    wordWrap: 'break-word',
                    marginRight: '2rem',
                  }}
                >
                  Total de Atendimentos
                </span>
                <span style={{ fontSize: '2.5rem', fontWeight: '600' }}>
                  {atendimentoData.totalAtendimento}
                </span>
              </div>
              <div
                style={{
                  width: '20.6rem',
                  height: '6.5rem',
                  backgroundColor: '#FFA300',
                  paddingLeft: '1.5rem',
                  paddingRight: '1.5rem',
                  paddingTop: '2rem',
                  paddingBottom: '2rem',
                  borderRadius: '0.68rem',
                  boxShadow: '0px 1px 8px 2px rgba(0, 0, 0, 0.25)',
                }}
                className="d-flex align-items-center text-white"
              >
                <span
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    wordWrap: 'break-word',
                    marginRight: '1rem',
                  }}
                >
                  Média de Atendimentos
                </span>
                <span style={{ fontSize: '2.5rem', fontWeight: '600' }}>
                  {atendimentoData.mediaAtendimento}
                </span>
              </div>
              <div
                style={{
                  width: '20.6rem',
                  height: '6.5rem',
                  backgroundColor: '#D9D9D9',
                  paddingLeft: '1.5rem',
                  paddingRight: '1.5rem',
                  paddingTop: '2rem',
                  paddingBottom: '2rem',
                  borderRadius: '0.68rem',
                  boxShadow: '0px 1px 8px 2px rgba(0, 0, 0, 0.25)',
                }}
                className="d-flex align-items-center"
              >
                <span
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    wordWrap: 'break-word',
                  }}
                >
                  Máximo de Atendimentos p/ dia
                </span>
                <span style={{ fontSize: '2.5rem', fontWeight: '600' }}>
                  {atendimentoData.maximoAtendimentoDia}
                </span>
              </div>
            </div>
            <div
              className="w-100 mt-3"
              style={{
                height: '100%',
                borderRadius: '0.68rem',

                boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
              }}
            >
              <ReactApexChart
                options={
                  indexAtual === 0
                    ? dataChartBarUsuario.options
                    : dataChartBar.options
                }
                series={
                  indexAtual === 0
                    ? dataChartBarUsuario.series
                    : dataChartBar.series
                }
                type="bar"
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </section>
        <section
          className="w-100 ms-3 d-flex"
          style={{
            height: '100%',
            maxHeight: '100%',
            borderRadius: '0.68rem',
            boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
          }}
        >
          {showDashboardFeedback ? (
            <DashboardFeedback
              atendimentoData={atendimentoData}
              setShowDashboardFeedback={setShowDashboardFeedback}
            />
          ) : (
            <DashboardUserAtendiment
              atendimentoData={atendimentoData}
              setShowDashboardFeedback={setShowDashboardFeedback}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
